/**
 * RenewaSkin cart drawer — copy-theme patch 2026-10-13
 * Keeps Dawn's CartDrawer interface for native product-form.js integration.
 * Cart mutations always go through Shopify Ajax; only product IDs control upsell eligibility.
 */
(() => {
  'use strict';
  if (customElements.get('cart-drawer')) return;

  const SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const endpoint = (path) => SHOP_ROOT.replace(/\/?$/, '/') + path;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
  const money = (cents, currency) => {
    const iso = currency || document.querySelector('cart-drawer')?.dataset.rsCurrency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: iso
      }).format(Number(cents || 0) / 100);
    } catch (_) { return '$' + (Number(cents || 0) / 100).toFixed(2); }
  };

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.lastTrigger = null;
      this.selectedProduct = null;
      this.purchaseType = 'onetime';
      this.selectedVariantId = null;
      this.pending = false;
      this._refreshPromise = null;
      this._lastCart = null;
      this._externalRefreshTimer = null;
      this._modalTrigger = null;
      this.addEventListener('click', (event) => this.handleClick(event));
      this.addEventListener('scroll', (event) => {
        if (event.target?.matches?.('[data-rs-track]')) this.updateCarouselArrows();
      }, true);
      this._onResize = () => this.updateCarouselArrows();
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (this.selectorOpen()) this.closeSelector();
          else this.close();
        }
      });
    }

    connectedCallback() {
      this.setHeaderCartIconAccessibility();
      this.readConfig();
      // The gift guard emits this after it reconciles gift lines. Refresh without
      // triggering another gift-guard change event (prevents event loops).
      this._giftListener = (event) => {
        if (event.detail?.source !== 'renewacell-gift-guard') return;
        this.scheduleRefresh();
      };
      document.addEventListener('cart-drawer:refresh', this._giftListener);
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this._unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'rs-cart-drawer') return;
          this.scheduleRefresh();
        });
      }
      window.addEventListener('resize', this._onResize);
      this.updateRecommendationsFromCart().catch(() => {});
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('cart-drawer:refresh', this._giftListener);
      this._unsubscriber?.();
      if (this._externalRefreshTimer) clearTimeout(this._externalRefreshTimer);
    }

    readConfig() {
      this.catalog = new Map();
      this.querySelectorAll('[data-rs-product-json]').forEach((script) => {
        try {
          const product = JSON.parse(script.textContent);
          this.catalog.set(String(product.id), product);
        } catch (error) { console.error('[RS Cart] Product data error:', error); }
      });
      try {
        this.renewacellConfig = JSON.parse(this.querySelector('[data-rs-renewacell-config]')?.textContent || '{}');
      } catch (_) { this.renewacellConfig = {}; }
    }

    setHeaderCartIconAccessibility() {
      const link = document.getElementById('cart-icon-bubble');
      if (!link || link.dataset.rsDrawerBound === '1') return;
      link.dataset.rsDrawerBound = '1';
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (event) => { event.preventDefault(); this.open(link); });
      link.addEventListener('keydown', (event) => {
        if (event.code === 'Space') { event.preventDefault(); this.open(link); }
      });
    }

    setActiveElement(element) { this.lastTrigger = element || document.activeElement; }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.classList.add('animate');
      requestAnimationFrame(() => this.classList.add('active'));
      document.body.classList.add('overflow-hidden');
      const inner = this.querySelector('.drawer__inner');
      setTimeout(() => {
        if (!this.classList.contains('active')) return;
        const focus = this.querySelector('.rs-cd-close');
        if (typeof trapFocus === 'function' && inner && focus) trapFocus(inner, focus);
        else focus?.focus();
      }, 250);
      this.updateRecommendationsFromCart().catch(() => {});
      requestAnimationFrame(() => this.updateCarouselArrows());
    }

    close() {
      if (this.selectorOpen()) this.closeSelector();
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.lastTrigger);
      else this.lastTrigger?.focus?.();
    }

    selectorOpen() { return !!this.querySelector('[data-rs-selector]:not([hidden])'); }

    closeSelector() {
      const modal = this.querySelector('[data-rs-selector]');
      if (modal) modal.hidden = true;
      this.querySelector('.rs-cd-inner')?.classList.remove('rs-cd--selecting');
      this.selectedProduct = null;
      this._modalTrigger?.focus?.();
    }

    handleClick(event) {
      const target = event.target.closest('button, [data-rs-close], [data-rs-select-product]');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-rs-close-selector]')) return this.closeSelector();
      if (target.matches('[data-rs-close]')) return this.close();
      if (target.matches('[data-rs-select-product]')) {
        event.preventDefault();
        return this.openSelector(target.dataset.rsSelectProduct, target);
      }
      if (target.matches('[data-rs-purchase]')) {
        this.purchaseType = target.dataset.rsPurchase;
        this.renderSelector();
        return;
      }
      if (target.matches('[data-rs-choose-variant]')) {
        this.selectedVariantId = String(target.dataset.rsChooseVariant);
        this.renderSelector();
        return;
      }
      if (target.matches('[data-rs-selector-add]')) return this.addSelectedBundle();
      if (target.matches('[data-rs-qty]')) return this.changeLine(target, Number(target.dataset.rsQty));
      if (target.matches('[data-rs-remove]')) return this.changeLine(target, null);
      if (target.matches('[data-rs-scroll-prev], [data-rs-scroll-next]')) {
        const track = this.querySelector('[data-rs-track]');
        const direction = target.hasAttribute('data-rs-scroll-next') ? 1 : -1;
        if (track) {
          const firstVisible = Array.from(track.querySelectorAll('.rs-cd-upsell')).find(card => !card.hidden);
          const step = (firstVisible?.getBoundingClientRect().width || track.clientWidth * .84) + 10;
          track.scrollBy({ left: direction * step, behavior: 'smooth' });
        }
      }
    }

    async json(url, body) {
      const init = { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' };
      if (body !== undefined) {
        init.method = 'POST';
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      const res = await fetch(url, init);
      const data = await res.json();
      if (!res.ok || data.status >= 400) {
        throw new Error(data.description || data.message || 'Unable to update cart. Please try again.');
      }
      return data;
    }

    async fetchCart() { return this.json(endpoint('cart.js')); }

    async updateRecommendationsFromCart(cart = null) {
      const latest = cart || await this.fetchCart();
      this._lastCart = latest;
      const present = new Set((latest.items || []).map((item) => String(item.product_id)));
      const section = this.querySelector('[data-rs-recommendations]');
      if (!section) return latest;
      const cards = Array.from(section.querySelectorAll('[data-rs-product-id]'));
      cards.forEach((card) => { card.hidden = present.has(card.dataset.rsProductId); });
      const visible = cards.filter((card) => !card.hidden);
      section.hidden = visible.length === 0;
      const controls = section.querySelector('.rs-cd-carousel-controls');
      if (controls) controls.hidden = visible.length < 2;
      // Snap to the start when the visible product set has changed.
      if (section.querySelector('[data-rs-track]') && visible.length < 2) {
        section.querySelector('[data-rs-track]').scrollLeft = 0;
      }
      requestAnimationFrame(() => this.updateCarouselArrows());
      return latest;
    }

    updateCarouselArrows() {
      const section = this.querySelector('[data-rs-recommendations]');
      const track = section?.querySelector('[data-rs-track]');
      const controls = section?.querySelector('.rs-cd-carousel-controls');
      if (!controls || !track || controls.hidden) return;
      if (!track.clientWidth) return; // closed drawer: check again upon opening
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const prev = controls.querySelector('[data-rs-scroll-prev]');
      const next = controls.querySelector('[data-rs-scroll-next]');
      if (prev) prev.hidden = track.scrollLeft <= 4;
      if (next) next.hidden = maxScroll <= 4 || track.scrollLeft >= maxScroll - 4;
    }

    notifyCartChange(cart, notifyGuard = true) {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rs-cart-drawer', cartData: cart });
      }
      if (notifyGuard) {
        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: { cart, source: 'rs-cart-drawer' }, bubbles: true
        }));
      }
    }

    showMessage(message) {
      const node = this.querySelector('[data-rs-message]');
      if (!node) return;
      node.textContent = message;
      node.hidden = !message;
      if (message) setTimeout(() => { if (node.textContent === message) node.hidden = true; }, 5200);
    }

    async changeLine(button, delta) {
      if (this.pending) return;
      const item = button.closest('[data-rs-item-key]');
      if (!item || item.dataset.rsGift === 'true') return;
      const key = item.dataset.rsItemKey;
      const current = Number(item.querySelector('.rs-cd-qty span')?.textContent || 0);
      const quantity = delta === null ? 0 : Math.max(0, current + delta);
      if (!key || current === quantity) return;
      this.pending = true;
      item.classList.add('rs-cd-item--updating');
      try {
        const cart = await this.json(endpoint('cart/change.js'), { id: key, quantity });
        await this.refresh();
        this.notifyCartChange(cart);
      } catch (error) { this.showMessage(error.message); }
      finally { this.pending = false; }
    }

    getSectionsToRender() {
      // The drawer is a Liquid snippet, not a Shopify section. Only request
      // independently renderable sections from cart/add.js; refresh the drawer
      // from a fresh page response. This avoids null cart-drawer HTML.
      return [{ id: 'cart-icon-bubble' }];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      if (!html) return '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(selector)?.innerHTML || '';
    }

    async fetchSections() {
      // The custom cart is inserted using {% render 'cart-drawer' %} in
      // theme.liquid. Shopify's Section Rendering API cannot refresh it as a
      // standalone section. Fetch the CURRENT preview page's server-rendered
      // markup (same cookies, market, theme preview) and extract #CartDrawer.
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      url.searchParams.set('_rs_cart_refresh', String(Date.now()));
      const response = await fetch(url.toString(), {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Could not refresh the cart drawer.');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const drawer = parsed.querySelector('#CartDrawer');
      if (!drawer) throw new Error('The cart drawer is missing from the refreshed theme page.');
      const headerIcon = parsed.getElementById('cart-icon-bubble');
      return {
        'cart-drawer': drawer.outerHTML,
        'cart-icon-bubble': headerIcon ? headerIcon.outerHTML : ''
      };
    }

    applySections(sections) {
      const html = sections?.['cart-drawer'];
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('#CartDrawer');
      const old = this.querySelector('#CartDrawer');
      if (!fresh || !old) return false;
      const scrollBefore = old.querySelector('[data-rs-scroll]')?.scrollTop || 0;
      old.replaceWith(fresh);
      this.classList.toggle('is-empty', fresh.dataset.rsCartCount === '0');
      if (this.classList.contains('active')) {
        const scroll = fresh.querySelector('[data-rs-scroll]');
        if (scroll) scroll.scrollTop = scrollBefore;
      }
      const headerHtml = sections?.['cart-icon-bubble'];
      if (headerHtml) {
        const headerParsed = new DOMParser().parseFromString(headerHtml, 'text/html');
        const nextIcon = headerParsed.getElementById('cart-icon-bubble');
        const oldIcon = document.getElementById('cart-icon-bubble');
        if (nextIcon && oldIcon) {
          oldIcon.replaceWith(nextIcon);
          this.setHeaderCartIconAccessibility();
        }
      }
      return true;
    }

    refresh(options = {}) {
      if (this._refreshPromise) return this._refreshPromise;
      this._refreshPromise = (async () => {
        let applied = this.applySections(options.sections);
        if (!applied) {
          const sections = await this.fetchSections();
          applied = this.applySections(sections);
        }
        if (!applied) {
          // Never show stale totals after a cart mutation.
          throw new Error('Cart sections could not be refreshed. Check the theme cart section configuration.');
        }
        await this.updateRecommendationsFromCart();
        if (options.open) this.open();
        return true;
      })().finally(() => { this._refreshPromise = null; });
      return this._refreshPromise;
    }

    renderContents(parsedState) {
      return this.refresh({ sections: parsedState?.sections, open: true }).catch((error) => {
        console.error('[RS Cart] Could not display updated cart:', error);
        this.showMessage(error.message);
        this.open();
      });
    }

    scheduleRefresh() {
      if (this._externalRefreshTimer) clearTimeout(this._externalRefreshTimer);
      this._externalRefreshTimer = setTimeout(() => {
        this.refresh().catch((error) => console.warn('[RS Cart] Refresh:', error));
      }, 140);
    }

    async openSelector(productId, trigger) {
      if (this.pending) return;
      const product = this.catalog.get(String(productId));
      if (!product) return this.showMessage('This product is currently unavailable.');
      try {
        const cart = await this.fetchCart();
        await this.updateRecommendationsFromCart(cart);
        if (cart.items?.some((item) => String(item.product_id) === String(product.id))) {
          return this.showMessage('This product is already in your cart.');
        }
      } catch (error) { return this.showMessage(error.message); }
      this._modalTrigger = trigger;
      this.selectedProduct = product;
      this.purchaseType = 'onetime';
      const offers = this.getOffers();
      // Default to the entry-level bundle (1 month / device-only), not the
      // first variant. Shopify variants are frequently ordered largest-first.
      // Keep subscription selling plans and RenewaCell's tier mapping intact.
      const eligible = offers.filter((v) => v.available);
      const starter = eligible.find((v) => v.tier === 'one') ||
        eligible.find((v) => /(?:^|\b)1\s*months?\b|\bonly\b/i.test(v.title || '')) ||
        eligible.slice().sort((a, b) => Number(a.price) - Number(b.price))[0] || offers[0];
      this.selectedVariantId = String(starter?.id || '');
      this.renderSelector();
      const selector = this.querySelector('[data-rs-selector]');
      selector.hidden = false;
      // Modal's scroll container persists between products; never reopen
      // halfway down the previous product's options.
      const scroll = selector.querySelector('.rs-cd-selector__scroll');
      if (scroll) scroll.scrollTop = 0;
      this.querySelector('.rs-cd-inner')?.classList.add('rs-cd--selecting');
      this.querySelector('[data-rs-close-selector]')?.focus();
    }

    getOffers() {
      const p = this.selectedProduct;
      if (!p) return [];
      const config = this.renewacellConfig;
      if (String(p.id) !== String(config?.productId)) {
        return p.variants.map((variant, index) => ({
          ...variant, tier: String(index),
          title: variant.label === 'Default Title' ? p.title : variant.label,
          subtitle: '', sellingPlanId: null
        }));
      }
      return (config.bundles || []).map((bundle, index) => {
        const variantId = this.purchaseType === 'subscription' ? bundle.subscription : bundle.onetime;
        const variant = p.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return null;
        const plan = this.purchaseType === 'subscription'
          ? variant.plans?.find((v) => String(v.id) === String(bundle.plan)) : null;
        return {
          ...variant, tier: bundle.tier, index,
          title: bundle.label, subtitle: bundle.description,
          price: this.purchaseType === 'subscription' && plan ? plan.price : variant.price,
          compare: this.purchaseType === 'subscription' && plan ? (variant.compare > (plan.price || 0) ? variant.compare : plan.compare) : variant.compare,
          available: variant.available && (this.purchaseType !== 'subscription' || !!plan),
          sellingPlanId: this.purchaseType === 'subscription' ? bundle.plan : null,
          giftsAllowed: bundle.giftsAllowed
        };
      }).filter(Boolean);
    }

    renderSelector() {
      const p = this.selectedProduct;
      const modal = this.querySelector('[data-rs-selector]');
      if (!p || !modal) return;
      const title = modal.querySelector('[data-rs-selector-title]');
      const base = modal.querySelector('[data-rs-selector-base]');
      const image = modal.querySelector('[data-rs-selector-image]');
      title.textContent = p.title;
      image.src = p.image || '';
      image.alt = p.title;
      // Use the store's own Judge.me badge only when the product has real review data.
      const rating = modal.querySelector('[data-rs-selector-rating]');
      const ratingMarkup = Array.from(this.querySelectorAll('.rs-cd-upsell'))
        .find((card) => card.dataset.rsProductId === String(p.id))
        ?.querySelector('.rs-cd-upsell__rating')?.innerHTML;
      if (rating) {
        rating.hidden = !ratingMarkup;
        rating.innerHTML = ratingMarkup || '';
      }
      const rc = String(p.id) === String(this.renewacellConfig?.productId);
      const tabs = modal.querySelector('[data-rs-selector-tabs]');
      tabs.hidden = !rc;
      tabs.querySelectorAll('[data-rs-purchase]').forEach((tab) => {
        const active = tab.dataset.rsPurchase === this.purchaseType;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      const offers = this.getOffers();
      if (!offers.some((offer) => String(offer.id) === this.selectedVariantId && offer.available)) {
        const preferred = offers.find((v) => v.tier === 'one' && v.available) || offers.find((v) => v.available);
        this.selectedVariantId = String(preferred?.id || '');
      }
      const selected = offers.find((offer) => String(offer.id) === this.selectedVariantId);
      base.textContent = selected ? money(selected.price, this._lastCart?.currency) : '';
      const list = modal.querySelector('[data-rs-selector-variants]');
      list.innerHTML = offers.map((offer, index) => {
        const selectedOffer = String(offer.id) === this.selectedVariantId;
        const compare = Number(offer.compare) > Number(offer.price)
          ? `<s>${esc(money(offer.compare, this._lastCart?.currency))}</s>` : '';
        const saving = Number(offer.compare) > Number(offer.price) && Number(offer.compare) > 0
          ? `<small class="rs-cd-selector__savings">YOU SAVE ${Math.round((offer.compare - offer.price) / offer.compare * 100)}%</small>` : '';
        return `<button type="button" role="radio" aria-checked="${selectedOffer}" data-rs-choose-variant="${esc(offer.id)}" class="rs-cd-selector__option ${selectedOffer ? 'is-selected' : ''}" ${offer.available ? '' : 'disabled'}>
          ${rc && offer.tier === 'three' && offers.length > 1 ? '<span class="rs-cd-selector__popular">Most Popular</span>' : ''}
          <span class="rs-cd-selector__radio"></span>
          <span class="rs-cd-selector__option-image">${offer.image || p.image ? `<img src="${esc(offer.image || p.image)}" alt="" class="fk-disable-lazy" loading="lazy">` : ''}</span>
          <span class="rs-cd-selector__option-title"><strong>${esc(offer.title)}</strong>${offer.subtitle ? `<small>${esc(offer.subtitle)}</small>` : ''}</span>
          <span class="rs-cd-selector__option-prices"><strong>${esc(money(offer.price, this._lastCart?.currency))}</strong>${compare}${saving}</span>
          ${offer.available ? '' : '<span class="rs-cd-selector__soldout">Sold out</span>'}
        </button>`;
      }).join('');
      const submit = modal.querySelector('[data-rs-selector-add]');
      submit.disabled = !selected?.available || this.pending;
      modal.querySelector('[data-rs-selector-button-price]').textContent = selected ? `· ${money(selected.price, this._lastCart?.currency)}` : '';
      modal.querySelector('[data-rs-selector-error]').hidden = true;
    }

    async addSelectedBundle() {
      if (this.pending || !this.selectedProduct) return;
      const p = this.selectedProduct;
      const offer = this.getOffers().find((v) => String(v.id) === this.selectedVariantId);
      if (!offer?.available) return;
      const errorBox = this.querySelector('[data-rs-selector-error]');
      const button = this.querySelector('[data-rs-selector-add]');
      this.pending = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      errorBox.hidden = true;
      try {
        const cart = await this.fetchCart();
        if (cart.items?.some((item) => String(item.product_id) === String(p.id))) {
          this.closeSelector();
          await this.updateRecommendationsFromCart(cart);
          return this.showMessage('This product is already in your cart.');
        }
        const items = [];
        const config = this.renewacellConfig;
        if (String(p.id) === String(config?.productId)) {
          const group = 'rc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
          const giftIds = (config.gifts || []).filter((id) => Number(id) > 0);
          const giftsAllowed = Boolean(offer.giftsAllowed && giftIds.length);
          const type = 'rc_' + (this.purchaseType === 'subscription' ? 'subscription' : 'onetime') + '_' + offer.tier;
          const parent = {
            id: Number(offer.id), quantity: 1,
            properties: {
              '_bundle_type': type,
              '_RC Bundle Parent': 'Yes',
              '_RC Bundle Group': group,
              '_RC Bundle Tier': offer.tier,
              '_RC Purchase Type': this.purchaseType,
              '_RC Gifts Allowed': giftsAllowed ? 'Yes' : 'No',
              '_RC Gift Variant IDs': giftsAllowed ? giftIds.join(',') : ''
            }
          };
          if (this.purchaseType === 'subscription') parent.selling_plan = Number(offer.sellingPlanId);
          items.push(parent);
          if (giftsAllowed) giftIds.forEach((id) => items.push({
            id: Number(id), quantity: 1,
            properties: {
              '_Free Gift': 'Yes', '_bundle_gift': 'FREE GIFT',
              '_bundle_type': type, '_RC Bundle Gift': 'Yes',
              '_RC Bundle Group': group, '_RC Parent Variant': String(offer.id)
            }
          }));
        } else {
          items.push({ id: Number(offer.id), quantity: 1 });
        }
        const result = await this.json(endpoint('cart/add.js'), { items });
        this.closeSelector();
        await this.refresh({ open: true });
        this.notifyCartChange(result);
      } catch (error) {
        errorBox.textContent = error.message || 'Please try again.';
        errorBox.hidden = false;
      } finally {
        this.pending = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  // Universal section uses a standard {% form 'product' %} without <product-form>.
  // Keep this scoped so Dawn/app product forms continue running their own handlers.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('rs-product__form')) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button?.disabled || form.dataset.rsSubmitting === '1') return;
    const formData = new FormData(form);
    form.dataset.rsSubmitting = '1';
    if (button) button.disabled = true;
    try {
      formData.set('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
      formData.set('sections_url', location.pathname);
      drawer.setActiveElement(button || document.activeElement);
      const response = await fetch(endpoint('cart/add.js'), {
        method: 'POST', body: formData, credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Unable to add product.');
      drawer.renderContents(data);
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rs-product-form', cartData: data });
      }
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { source: 'rs-product-form' } }));
    } catch (error) {
      drawer.showMessage(error.message || 'Unable to add product.');
      drawer.open(button);
    } finally {
      form.dataset.rsSubmitting = '0';
      if (button) button.disabled = false;
    }
  });
})();
